import { useCallback, useEffect, useRef, useState } from "react";
import { Message, useMessagesStore } from "@/entities/message";
import { User } from "@/entities/user";
import { useScrollStore } from "./use-scroll-store";
import { useChatStore } from "@/entities/chat";

interface UseMessageScrollOptions {
  chatId: string | undefined;
  messages: Message[] | null;
  chatContainerRef: React.RefObject<HTMLDivElement | null>;
  user: User | null;
}

export const useMessageScroll = ({
  chatId,
  messages,
  chatContainerRef,
  user,
}: UseMessageScrollOptions) => {
  const scrollEndTimeoutRef = useRef<number | null>(null);
  const firstUnreadIndexRef = useRef<Record<string, number | null>>({});
  const scrollPositionsRef = useRef<Record<string, number | undefined>>({});
  const isProgrammaticScroll = useRef(false);

  const [isRapidClicking, setIsRapidClicking] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null);
  const resetChat = useScrollStore((state) => state.resetChat);
  const setShowScrollDown = useScrollStore((state) => state.setShowScrollDown);

  const hasInitializedRef = useRef<Record<string, boolean>>({});

  const loadMessageById = useMessagesStore((state) => state.loadMessageById);
  const loadingMessageById = useMessagesStore((state) => state.loadingMessageById);
  const pages = useMessagesStore((state) => state.pages);

  // 1. Сброс при смене чата
  useEffect(() => {
    if (chatId) {
      hasInitializedRef.current[chatId] = false;
    }

    if (chatId) {
      firstUnreadIndexRef.current[chatId] = null;
      // hasInitializedRef.current = false

      // if (chatContainerRef.current ) {
      //   scrollPositionsRef.current[chatId] = chatContainerRef.current.scrollTop;
      // }

      resetChat(chatId);
    }

    // userHasScrolledManually.current = chatId, false;
  }, [chatId]);

  // --- 2. Инициализация данных для текущего чата ---
  // Локальный ref только для этого useEffect
  const firstMessageInfoRef = useRef<{
    firstDate: Date;
    firstUnreadId: string | null;
  } | null>(null);

  useEffect(() => {
    if (!chatId || !messages?.length || !user || !chatContainerRef.current)
      return;

    const container = chatContainerRef.current;

    // если скролла нет — выходим
    if (container.scrollHeight <= container.clientHeight) return;

    const firstMsg = messages[0];
    const firstMsgDate = new Date(firstMsg.createdAt);

    // -Первый вход в чат-
    if (!firstUnreadIndexRef.current[chatId]) {
      const idx = messages.findIndex(
        (msg) => msg.owner._id !== user._id && !msg.readedAt
      );

      firstUnreadIndexRef.current[chatId] = idx === -1 ? null : idx;

      firstMessageInfoRef.current = {
        firstDate: firstMsgDate,
        firstUnreadId: idx !== -1 ? messages[idx]._id : null,
      };

      return;
    }

    // -Проверяем, подгрузились ли старые сообщения сверху-
    const prevInfo = firstMessageInfoRef.current;

    if (prevInfo && firstMsgDate < prevInfo.firstDate) {
      const firstUnreadId = prevInfo.firstUnreadId;

      // Если прежнего непрочитанного сообщения больше нет — сбрасываем индекс
      const stillExists = messages.some((m) => m._id === firstUnreadId);
      if (!stillExists) {
        firstUnreadIndexRef.current[chatId] = null;
      } else {
        const newIdx = messages.findIndex((m) => m._id === firstUnreadId);
        firstUnreadIndexRef.current[chatId] = newIdx === -1 ? null : newIdx;
      }

      // Обновляем сохранённую дату самой старой верхней записи
      firstMessageInfoRef.current!.firstDate = firstMsgDate;
    }
  }, [chatId, messages, user, chatContainerRef]);

  const scrollToInitial = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container || !chatId) return;

    isProgrammaticScroll.current = true;

    const savedPos = scrollPositionsRef.current[chatId];
    const unreadIdx = firstUnreadIndexRef.current[chatId];

    const children = Array.from(container.children);

    if (typeof savedPos === "number" && savedPos > 0) {
      container.scrollTop = Math.min(
        savedPos,
        container.scrollHeight - container.clientHeight
      );
    } else if (
      unreadIdx !== null &&
      unreadIdx !== undefined &&
      unreadIdx >= 0 &&
      children[unreadIdx]
    ) {
      const el = children[unreadIdx] as HTMLElement;
      container.scrollTop = Math.max(0, el.offsetTop - 200);
    } else {
      container.scrollTop = container.scrollHeight;
    }

    // небольшой таймаут, чтобы сбросить флаг после завершения события
    requestAnimationFrame(() => {
      isProgrammaticScroll.current = false;
    });

    hasInitializedRef.current[chatId] = true;
  }, [chatId, chatContainerRef]);

  useEffect(() => {
    // сбрасываем инициализацию при смене чата
    if (!chatId) return;

    // если нет сохраненной позиции → сбросим флаг и будем скроллить заново
    if (!scrollPositionsRef.current) {
      hasInitializedRef.current[chatId] = false;
    }

    // делаем автоскролл при смене чата
    scrollToInitial();
  }, [chatId, messages?.length]);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!chatId || !container) return;

    const handleScroll = () => {
      if (isProgrammaticScroll.current) return;

      const scrollBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;

      setShowScrollDown(chatId, scrollBottom > 300);

      if (scrollBottom > 10) {
        scrollPositionsRef.current[chatId] = container.scrollTop;
      } else {
        // если внизу — сбрасываем сохранённую позицию (всегда будет скроллиться в конец)
        // scrollPositionsRef.current[chatId] = undefined;
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => container.removeEventListener("scroll", handleScroll);
  }, [chatId, chatContainerRef, setShowScrollDown]);

  // Автопрокрутка при добавлении новых сообщений
  const prevMessagesLengthRef = useRef<number>(0);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container || !messages || !chatId) {
      prevMessagesLengthRef.current = messages?.length ?? 0;
      return;
    }

    const currentLength = messages.length;
    const wasIncreased = currentLength > prevMessagesLengthRef.current;

    prevMessagesLengthRef.current = currentLength;

    if (wasIncreased) {
      const scrollBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;

      if (scrollBottom < 600) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  }, [messages, chatId, chatContainerRef]);

  const setScrollToMessage = useScrollStore(
    (state) => state.setScrollToMessage
  );

  const scrollToMessageRef = useRef(false);

  const scrollToMessage = useCallback(
    async (chatId: string, msgId: string) => {
      if (!chatId || !msgId) return;
      scrollToMessageRef.current = true;

      const messages = useMessagesStore.getState().messages[chatId];

      const container = chatContainerRef.current;
      if (!container) return;

      const messageExists = messages?.some((msg) => msg._id === msgId);

      if (!messageExists && !loadingMessageById[chatId]) {
        // Сообщение не найдено — загружаем его
        await loadMessageById(chatId, msgId);
        setHighlightedMessageId(msgId);
      }

      // Если сообщение существует (или было загружено), прокручиваемся к нему
      const targetElement = container.querySelector(
        `[data-msg-id="${msgId}"]`
      ) as HTMLElement | null;

      if (!targetElement || !container) return;

      setHighlightedMessageId(msgId);
      setIsRapidClicking(true);
      setTimeout(() => setIsRapidClicking(false), 500);

      if ("vibrate" in navigator) {
        navigator.vibrate(80);
      }

      const targetScrollTop = targetElement.offsetTop - 100;
      const isAlreadyVisible =
        targetScrollTop >= container.scrollTop - 50 &&
        targetScrollTop <= container.scrollTop + container.clientHeight + 50;

      if (!isAlreadyVisible) {
        container.scrollTo({
          top: targetScrollTop,
          behavior: "smooth",
        });
      }

      // Очистка предыдущего обработчика
      if (scrollEndTimeoutRef.current) {
        clearTimeout(scrollEndTimeoutRef.current);
      }

      const prevHandler = (container as any).__replyScrollHandler;
      if (prevHandler) {
        container.removeEventListener("scroll", prevHandler);
        delete (container as any).__replyScrollHandler;
      }

      const handleScroll = () => {
        if (scrollEndTimeoutRef.current) {
          clearTimeout(scrollEndTimeoutRef.current);
        }
        scrollEndTimeoutRef.current = window.setTimeout(() => {
          container.removeEventListener("scroll", handleScroll);
          delete (container as any).__replyScrollHandler;
          if (highlightedMessageId === msgId) {
            setTimeout(() => setHighlightedMessageId(null), 2000);
          }
        }, 150);
      };

      (container as any).__replyScrollHandler = handleScroll;
      container.addEventListener("scroll", handleScroll, { passive: true });

      const fallbackTimer = setTimeout(() => {
        if (highlightedMessageId === msgId) {
          container.removeEventListener("scroll", handleScroll);
          delete (container as any).__replyScrollHandler;
          setTimeout(() => setHighlightedMessageId(null), 2000);
        }
      }, 300);

      setTimeout(() => {
        scrollToMessageRef.current = false;
      }, 500);
      scrollEndTimeoutRef.current = fallbackTimer as unknown as number;
    },
    [
      chatContainerRef,
      highlightedMessageId,
      chatId,
      loadMessageById,
      loadingMessageById,
      messages
    ]
  );

  const scrollToBottom = useCallback(async () => {
    if (!chatId || !chatContainerRef.current) return;
    
    const chat = useChatStore.getState().getChat(chatId);
    const container = chatContainerRef.current;
    const lastMessageId = chat?.lastMessage?._id;

    const state = useMessagesStore.getState();
    const messages = state.messages[chatId];

    if (messages?.find((msg) => msg._id === lastMessageId)) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
      return;
    }

    const currentPages = state.pages[chatId];
    const currentPage = state.page[chatId];

    if (currentPages.includes(1)) {
      // Просто скроллим вниз
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    } else if (currentPages.includes(2)) {
      // Подгружаем первую страницу и скроллим вниз
      await state.loadMessages(chatId, 1);
      // После загрузки, скроллим вниз
      setTimeout(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    } else {
      // Запрашиваем заново сообщения с первой страницы и полностью заменяем стейт
      await state.loadMessages(chatId, 1, true);
      // После загрузки, скроллим вниз
      container.scrollTo({
        top: container.scrollHeight,
      });
    }

  }, [chatId, chatContainerRef]);

  // --- Эффект для обработки прокрутки к сообщению после загрузки ---
  useEffect(() => {
    if (!chatId || !messages || !chatContainerRef.current) return;

    const container = chatContainerRef.current;
    const targetElement = container.querySelector(
      `[data-msg-id="${highlightedMessageId}"]`
    ) as HTMLElement | null;

    // Если мы уже прокручивались к этому сообщению и оно теперь существует — прокручиваемся снова
    if (highlightedMessageId && targetElement) {
      const targetScrollTop = targetElement.offsetTop - 100;
      const isAlreadyVisible =
        targetScrollTop >= container.scrollTop - 50 &&
        targetScrollTop <= container.scrollTop + container.clientHeight + 50;

      if (!isAlreadyVisible) {
        container.scrollTo({
          top: targetScrollTop,
        });
      }
    }
  }, [messages, highlightedMessageId, chatContainerRef]);

  useEffect(() => {
    if (!chatId) return;

    setScrollToMessage(chatId, scrollToMessage);
    return () => {
      // Опционально: очистить при размонтировании
      setScrollToMessage(chatId, null);
    };
  }, [chatId, scrollToMessage, setScrollToMessage]);

  return {
    hasScrolled: chatId ? hasInitializedRef.current[chatId] : false,
    highlightedMessageId,
    isRapidClicking,
    scrollToBottom,
    scrollToMessage,
    firstUnreadIndex: chatId ? firstUnreadIndexRef.current[chatId] : null,
    scrollToMessageRef
  };
};
